import React, { useState, useEffect } from "react";
import useSWR from "swr";

import KPISummaryRow from "./KPISummaryRow";
import EmployeeDeepDiveModal from "./EmployeeDeepDiveModal";

import { KPIDashboardService } from "../service/kpiDashboardService";

import "../styles/KPIParametersSummaryTable.scss";

const KPIParametersSummaryTable = ({
  userId,
  onGeoBreakdownView,
  canViewBreakdown = true,
  setLastUpdated,
}) => {
  const [sortedData, setSortedData] = useState([]);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState(null);
  const [focusedRowIndex, setFocusedRowIndex] = useState(null);
  const [viewPerformanceGraph, setViewPerformanceGraph] = useState(false);

  const { data, error } = useSWR(
    `kpi-parameters-${userId}`,
    () => {
      return KPIDashboardService.kpiSummaryFetcher({ userId });
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const asOfDate = data?.as_of_date;

  useEffect(() => {
    if (!!asOfDate && !!setLastUpdated) {
      setLastUpdated(asOfDate);
    }
  }, [asOfDate, setLastUpdated]);

  useEffect(() => {
    if (sortField && sortDir && data?.metrics) {
      const sorted = [...data.metrics].sort((a, b) => {
        const av = sortField === "ach_pct" ? a.ach_pct : a.growth_pct;
        const bv = sortField === "ach_pct" ? b.ach_pct : b.growth_pct;
        return sortDir === "asc" ? av - bv : bv - av;
      });
      setSortedData(sorted);
    } else if (data?.metrics) {
      setSortedData(data.metrics);
    }
  }, [sortField, sortDir, data]);

  const handleSort = (field) => {
    if (sortField !== field || !sortDir) {
      setSortField(field);
      setSortDir("asc");
      return;
    }

    if (sortDir === "asc") {
      setSortDir("desc");
      return;
    }

    setSortField(null);
    setSortDir(null);
  };

  const onPerformanceGraphView = () => {
    setViewPerformanceGraph(true);
  };

  const overallAchPct = data?.overall_achievement_pct || 0;
  const loading = !data && !error;

  return (
    <div className="kpi-table-card">
      <div className="scroll-container">
        <div className="table-content">
          <KPISummaryRow
            variant="overall"
            rowIndex={0}
            overallAchPct={overallAchPct}
            focusedRowIndex={focusedRowIndex}
            setFocusedRowIndex={setFocusedRowIndex}
            onGeoBreakdownView={onGeoBreakdownView}
            onPerformanceGraphView={onPerformanceGraphView}
            canViewBreakdown={canViewBreakdown}
          />

          <KPISummaryRow
            variant="header"
            sortField={sortField}
            sortDir={sortDir}
            handleSort={handleSort}
          />
          {loading ? (
            <SummaryRowSkeleton />
          ) : (
            sortedData.map((row, i) => (
              <KPISummaryRow
                key={i}
                row={row}
                rowIndex={i + 1}
                focusedRowIndex={focusedRowIndex}
                setFocusedRowIndex={setFocusedRowIndex}
                onGeoBreakdownView={onGeoBreakdownView}
                onPerformanceGraphView={onPerformanceGraphView}
                canViewBreakdown={canViewBreakdown}
                isEmployeeDeepDiveOpen={viewPerformanceGraph}
              />
            ))
          )}
          {viewPerformanceGraph && (
            <EmployeeDeepDiveModal
              userData={{
                userId,
                username: "",
              }}
              employeeDataType="performance-graph"
              setViewEmployeeDeepDive={setViewPerformanceGraph}
              metric={sortedData[focusedRowIndex - 1]?.code}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const SummaryRowSkeleton = () => {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        padding: "12px 16px",
      }}
    >
      {Array.from({ length: 8 }).map((_, i) => (
        <button
          key={i}
          style={{
            height: "32px",
            width: "100%",
            backgroundColor: "#E0E0E0",
            border: "none",
            borderRadius: "4px",
          }}
          disabled
        >
          &nbsp;
        </button>
      ))}
    </div>
  );
};

export default KPIParametersSummaryTable;
